import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { Layout } from '@/components/Layout'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { RequireAuth } from '@/components/RequireAuth'
import { CellarProvider } from '@/contexts/CellarContext'
import { CurrencyProvider } from '@/contexts/CurrencyContext'
import { useCellarContext } from '@/contexts/useCellarContext'

// Route-level code splitting — heavy pages loaded on demand
const CellarOverview = lazy(() => import('@/pages/CellarOverview'))
const SectionView = lazy(() => import('@/pages/SectionView'))
const BottleList = lazy(() => import('@/pages/BottleList'))
const Analytics = lazy(() => import('@/pages/Analytics'))
const WineMap = lazy(() => import('@/pages/WineMap'))
const SyncPage = lazy(() => import('@/pages/SyncPage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const CellarStory = lazy(() => import('@/pages/CellarStory'))
const CellarWrapped = lazy(() => import('@/pages/CellarWrapped'))
const SharedCellarView = lazy(() => import('@/pages/SharedCellarView'))
const CellarManagement = lazy(() => import('@/pages/CellarManagement'))
const AddCellarPage = lazy(() => import('@/pages/AddCellarPage'))
const CellarShowcase = lazy(() => import('@/pages/CellarShowcase'))
const Explore = lazy(() => import('@/pages/Explore'))
const Discover = lazy(() => import('@/pages/Discover'))
const ShowcasesPage = lazy(() => import('@/pages/ShowcasesPage'))
const ShowcaseBuilder = lazy(() => import('@/pages/ShowcaseBuilder'))
const ShowcasePublicView = lazy(() => import('@/pages/ShowcasePublicView'))
const TastingsPage = lazy(() => import('@/pages/TastingsPage'))
const TastingHostView = lazy(() => import('@/pages/TastingHostView'))
const TastingJoinView = lazy(() => import('@/pages/TastingJoinView'))
const NotFound = lazy(() => import('@/pages/NotFound'))

function AuthenticatedApp() {
  const { hasCellar, loading } = useCellarContext()

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <img
            src="/cellarwise_icon.png"
            alt=""
            className="h-12 w-12 animate-pulse rounded-lg"
          />
          <span className="font-heading text-sm text-muted-foreground">
            Loading...
          </span>
        </div>
      </div>
    )
  }

  if (!hasCellar) {
    return <Suspense fallback={null}><OnboardingPage /></Suspense>
  }

  return <Layout />
}

export default function App() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<Suspense fallback={null}><LoginPage /></Suspense>} />
        <Route path="/shared/:token" element={<Suspense fallback={null}><SharedCellarView /></Suspense>} />
        <Route path="/showcase/:token" element={<Suspense fallback={null}><ShowcasePublicView /></Suspense>} />
        <Route path="/cellar-showcase/:token" element={<Suspense fallback={null}><CellarShowcase /></Suspense>} />
        <Route path="/taste/:token" element={<Suspense fallback={null}><TastingJoinView /></Suspense>} />
        <Route path="/explore" element={<Suspense fallback={null}><Explore /></Suspense>} />
        <Route path="/discover" element={<Suspense fallback={null}><Discover /></Suspense>} />
        <Route element={<RequireAuth><CurrencyProvider><CellarProvider><AuthenticatedApp /></CellarProvider></CurrencyProvider></RequireAuth>}>
          <Route path="/" element={<CellarOverview />} />
          <Route path="/section/:id" element={<SectionView />} />
          <Route path="/bottles" element={<BottleList />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/map" element={<WineMap />} />
          <Route path="/sync" element={<SyncPage />} />
          <Route path="/story" element={<CellarStory />} />
          <Route path="/wrapped" element={<CellarWrapped />} />
          <Route path="/showcases" element={<ShowcasesPage />} />
          <Route path="/showcases/:id" element={<ShowcaseBuilder />} />
          <Route path="/tastings" element={<TastingsPage />} />
          <Route path="/tastings/:id" element={<TastingHostView />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/cellars" element={<CellarManagement />} />
          <Route path="/onboarding/new" element={<AddCellarPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </ErrorBoundary>
  )
}
